import Dexie from 'dexie';
import type { AuditEvent, Meta } from '@/domain/types';
import { rt } from './runtime';

/**
 * The single write path. Every change runs inside one storage transaction (all or nothing)
 * and leaves an entry in the history log with who, when, before and after.
 */

export class StorageError extends Error {
  readonly kind: 'quota' | 'blocked' | 'unknown';
  constructor(message: string, kind: StorageError['kind'], cause?: unknown) {
    super(message, { cause });
    this.name = 'StorageError';
    this.kind = kind;
  }
}

/** A rule was broken (e.g. staff changing an approved night). Shown to the person as is. */
export class DomainError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DomainError';
  }
}

type StorageListener = (e: StorageError) => void;
const storageListeners = new Set<StorageListener>();
export function onStorageError(l: StorageListener): () => void {
  storageListeners.add(l);
  return () => storageListeners.delete(l);
}

export function toStorageError(err: unknown): StorageError | null {
  const name = (err as { name?: string })?.name ?? '';
  const inner = (err as { inner?: { name?: string } })?.inner?.name ?? '';
  if (name === 'QuotaExceededError' || inner === 'QuotaExceededError') return new StorageError('This tablet is out of storage space. Save this week’s Excel file, then free up space.', 'quota', err);
  if (['DatabaseClosedError', 'InvalidStateError', 'OpenFailedError', 'MissingAPIError'].includes(name)) return new StorageError('The tablet’s storage can’t be used (private browsing, or site data blocked). Changes are NOT being saved.', 'blocked', err);
  return null;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function newId(prefix = ''): string {
  return `${prefix}${crypto.randomUUID()}`;
}

export function newMeta(id?: string): Meta {
  const now = nowIso();
  return { id: id ?? newId(), createdAt: now, updatedAt: now, version: 1 };
}

/** An updated copy: bumps the version and the time. */
export function touch<T extends Meta>(entity: T, patch: Partial<T> = {}): T {
  return { ...entity, ...patch, updatedAt: nowIso(), version: entity.version + 1 };
}

export interface AuditInput {
  action: string;
  entity: string;
  entityId: string;
  summary: string;
  before?: unknown;
  after?: unknown;
  reason?: string;
}

export async function writeAudit(a: AuditInput): Promise<void> {
  const r = rt();
  const event: AuditEvent = {
    id: newId('aud-'),
    at: nowIso(),
    actorId: r.actor.userId,
    actorName: r.actor.name,
    action: a.action,
    entity: a.entity,
    entityId: a.entityId,
    summary: a.summary,
    before: a.before ?? null,
    after: a.after ?? null,
    reason: a.reason ?? '',
  };
  await r.db.audit.add(event);
}

/**
 * Runs `fn` in one read-write transaction over the given tables plus the history log and the
 * outbox (so, with sharing on, every change is queued for the other devices in the same step).
 */
export async function inTx<T>(tableNames: string[], fn: () => Promise<T>): Promise<T> {
  const db = rt().db;
  try {
    return await db.transaction('rw', [...new Set([...tableNames, 'audit', 'outbox'])], fn);
  } catch (err) {
    if (err instanceof DomainError) throw err;
    const se = toStorageError(err);
    if (se) {
      storageListeners.forEach((l) => l(se));
      throw se;
    }
    if (err instanceof Dexie.DexieError) {
      const wrapped = new StorageError(`Could not save: ${err.message}`, 'unknown', err);
      storageListeners.forEach((l) => l(wrapped));
      throw wrapped;
    }
    throw err;
  }
}

/** Saves a record and logs it. Call inside inTx() with the table included. */
export async function putAudited<T extends Meta>(table: string, next: T, before: T | undefined | null, audit: { action: string; summary: string; reason?: string }): Promise<void> {
  await rt().db.table(table).put(next);
  await writeAudit({ ...audit, entity: table, entityId: next.id, before: before ?? null, after: next });
}
